import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Inter, the typeface HighLevel's own UI uses, bundled and served from our own
// origin — so the app looks native inside the CRM without a request to a font CDN.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@/app/globals.css";
import { DEFAULT_THEME, applyThemeVars } from "@/theme/tokens";

/**
 * The SPA entry point.
 *
 * ⚠️ The route PATHS are load-bearing. `/app_auth/app` is the custom-page URL
 * and `/app_auth/redirect/:id` the OAuth redirect URI, both registered in the
 * marketplace portal. Renaming either silently breaks every existing install:
 * the CRM renders a 404 inside its own chrome and reports nothing.
 *
 * Deep links only work because `public/_redirects` rewrites every path to
 * `index.html`. Without it, a hard refresh on `/app_auth/app` 404s at the CDN
 * before this file ever runs.
 */

/*
 * The default palette, before the first paint.
 *
 * The theme provider replaces these once it knows the agency, but that takes a
 * round trip — and without a starting value the app renders with every
 * `var(--primary)` unset, which is a page of black text on white with invisible
 * buttons. Done here rather than as a `:root` block in CSS so `tokens.ts` stays
 * the single source of truth on this side.
 */
applyThemeVars(DEFAULT_THEME);

/*
 * Lazy per route.
 *
 * These are unrelated surfaces that nobody visits in one session: bundling them
 * together means the person hitting the OAuth redirect downloads the whole
 * branding editor before the page can say "Installed".
 */
const Landing = lazy(() => import("@/app/page"));
const PrivacyPolicy = lazy(() => import("@/app/legal/privacy"));
const TermsOfService = lazy(() => import("@/app/legal/terms"));
const AppShell = lazy(() => import("@/app/app_auth/app/page"));
const InstallRedirect = lazy(() => import("@/app/app_auth/redirect/[id]/page"));
/** The public, white-label client report. Token-authorised; no SSO. */
const ClientReport = lazy(() => import("@/app/report/[token]/page"));

/** Shown only while a route chunk is in flight — deliberately plain. */
function RouteFallback() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background px-6 text-center">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Page not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          That address does not match anything in this app.
        </p>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* Linked from the Google OAuth consent screen: keep these paths. */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          {/* The GoHighLevel custom page. Registered in the portal. */}
          <Route path="/app_auth/app" element={<AppShell />} />
          {/* The marketplace OAuth redirect. `:id` is the app id. */}
          <Route path="/app_auth/redirect/:id" element={<InstallRedirect />} />
          <Route path="/report/:token" element={<ClientReport />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </React.StrictMode>
);
